import React, { useRef, Fragment } from 'react';
import { Unknown } from '../Unknown';
import { Box } from './Box';
import { BirdComboBox } from '../BirdComboBox';
import { BIRDVisualization } from '../BIRDVisualization';
import { BirdCheckboxList } from '../BirdCheckboxList';
import { BirdTextField } from '../BirdTextField';
import { BirdImage } from '../BirdImage';
import { BirdSlider } from '../BirdSlider';
import { BirdText } from '../BirdText';
import { BirdRadioList } from '../BirdRadioList';
import { BirdTable } from '../BirdTable';
import { BirdCrosstab } from '../BirdCrosstab';
import { BirdButtonBar } from '../BirdButtonBar';
import { StackContainer } from '../StackContainer';
import { PromptContainer } from '../PromptContainer';
import { BirdWebContent } from '../BirdWebContent';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { PositionedLayout } from '../PositionedLayout';
import { Title } from '../Title';
import { FlexHeightLayout, hasFlexibleHeight } from '../FlexHeightLayout';
import { exposeTestId } from '../../config';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ReportContextMenuState } from '../../store/reportContextMenu/ReportContextMenuState';
import { BirdJobContent } from '../BirdJobContent';
import { InfoBar } from '../InfoBar';
import { BirdBreadcrumbArea } from '../BirdBreadcrumb';
import { StatusLayout } from '../StatusLayout';
export function createChildren(childIds, state, childOptions) {
    if (!state || !childIds) {
        return null;
    }
    const children = [];
    for (const id of childIds) {
        children.push(React.createElement(Child, Object.assign({ key: id, id: id, state: state }, childOptions)));
    }
    return children;
}
const BaseChild = ({ id, state, includePreferredSizing, style, contentJustification, actions, }) => {
    const cbRef = useRef(null);
    const onReportContextMenu = (e) => {
        var _a;
        actions.setReportContextMenuTarget(id, e.nativeEvent.timeStamp);
        (_a = cbRef.current) === null || _a === void 0 ? void 0 : _a.call(cbRef, e);
    };
    const centerContent = contentJustification === 'center';
    const useCalculatedHeight = !!includePreferredSizing && hasFlexibleHeight(id, state);
    return (React.createElement(ErrorBoundary, { visualId: id, errorMessageKey: { key: 'ReportContainer.component.renderError.txt' } }, useCalculatedHeight ? (React.createElement(FlexHeightLayout, { style: style, id: id, onReportContextMenu: onReportContextMenu }, createChildWithStatusAndBreadcrumb(id, state, !!includePreferredSizing, true))) : (React.createElement(PositionedLayout, { style: style, id: id, autoLayout: includePreferredSizing, onReportContextMenu: onReportContextMenu, "test-id": exposeTestId() ? id : undefined, contentJustification: contentJustification },
        React.createElement(Title, { id: id }),
        createChildWithStatusAndBreadcrumb(id, state, !!includePreferredSizing, !centerContent)))));
};
export const Child = connect(undefined, (dispatch) => ({
    actions: bindActionCreators(ReportContextMenuState.actions, dispatch),
}))(BaseChild);
function createChild(id, state, includePreferredSizing) {
    if (state.ButtonBar[id]) {
        return React.createElement(BirdButtonBar, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.CheckboxList[id]) {
        return React.createElement(BirdCheckboxList, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.RadioList[id]) {
        return React.createElement(BirdRadioList, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.ComboBox[id]) {
        return React.createElement(BirdComboBox, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.Slider[id]) {
        return React.createElement(BirdSlider, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.TextField[id]) {
        return React.createElement(BirdTextField, { id: id, preferredSizing: includePreferredSizing });
    }
    if (state.Container[id]) {
        return React.createElement(Box, { id: id });
    }
    if (state.Crosstab[id]) {
        return React.createElement(BirdCrosstab, { id: id });
    }
    if (state.DataVisualizations[id]) {
        return React.createElement(BIRDVisualization, { id: id });
    }
    if (state.Image[id]) {
        return React.createElement(BirdImage, { id: id });
    }
    if (state.StackContainer[id]) {
        return React.createElement(StackContainer, { id: id });
    }
    if (state.PromptContainer[id]) {
        return React.createElement(PromptContainer, { id: id });
    }
    if (state.Table[id]) {
        return React.createElement(BirdTable, { id: id });
    }
    if (state.Text[id]) {
        return React.createElement(BirdText, { id: id });
    }
    if (state.WebContent[id]) {
        return React.createElement(BirdWebContent, { id: id });
    }
    if (state.JobContent[id]) {
        return React.createElement(BirdJobContent, { id: id });
    }
    if (state.InfoBar[id]) {
        return React.createElement(InfoBar, { id: id });
    }
    return React.createElement(Unknown, { id: id });
}
function createChildWithStatus(id, state, includePreferredSizing, setWrapperHeight) {
    const isContainer = !!state.Container[id];
    let style = {
        position: 'relative',
        width: '100%',
        height: setWrapperHeight ? '100%' : undefined,
        display: 'flex',
        outline: 'none',
    };
    if (setWrapperHeight) {
        style = Object.assign(Object.assign({}, style), { height: '100%', flexShrink: 1, overflow: 'hidden' });
    }
    return (React.createElement("div", { style: style },
        React.createElement(StatusLayout, { id: id, isContainer: isContainer }, createChild(id, state, includePreferredSizing))));
}
function createChildWithStatusAndBreadcrumb(id, state, includePreferredSizing, setWrapperHeight) {
    if (state.Crosstab[id] || state.DataVisualizations[id]) {
        return (React.createElement(Fragment, null,
            React.createElement(BirdBreadcrumbArea, { id: id }),
            createChildWithStatus(id, state, includePreferredSizing, setWrapperHeight)));
    }
    else {
        return createChildWithStatus(id, state, includePreferredSizing, setWrapperHeight);
    }
}
